/* ==========================================================================
   HYBEC AIR — brand interactions
   Scroll reveals, home loader, video play handler.
   All effects respect prefers-reduced-motion and pointer capability.
   ========================================================================== */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal ---------- */
  function initReveals() {
    var targets = document.querySelectorAll('.hy-reveal');
    if (!targets.length) return;

    if (reducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    targets.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---------- Count-up stat numbers ---------- */
  function initCounters() {
    var targets = document.querySelectorAll('.hy-count');
    if (!targets.length) return;

    var animate = function (el) {
      var raw = el.getAttribute('data-count-to');
      var target = parseFloat(raw);
      if (isNaN(target)) return;

      var duration = 1400;
      var start = null;

      var step = function (timestamp) {
        if (start === null) start = timestamp;
        var progress = Math.min((timestamp - start) / duration, 1);
        var eased = 1 - Math.pow(1 - progress, 3);
        var current = Math.round(target * eased);
        el.textContent = current;
        if (progress < 1) {
          window.requestAnimationFrame(step);
        } else {
          el.textContent = raw;
        }
      };
      window.requestAnimationFrame(step);
    };

    if (reducedMotion || !('IntersectionObserver' in window)) {
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            animate(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.6 }
    );
    targets.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---------- Pointer tilt for product/category cards ---------- */
  function initTilt() {
    if (reducedMotion || !window.matchMedia('(pointer: fine)').matches) return;

    var cards = document.querySelectorAll('.hy-tilt');
    cards.forEach(function (card) {
      var frame = null;

      var onMove = function (e) {
        var rect = card.getBoundingClientRect();
        var px = (e.clientX - rect.left) / rect.width - 0.5;
        var py = (e.clientY - rect.top) / rect.height - 0.5;
        if (frame) window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(function () {
          var rotY = (px * 8).toFixed(2);
          var rotX = (py * -8).toFixed(2);
          card.style.transform =
            'perspective(1000px) rotateX(' + rotX + 'deg) rotateY(' + rotY + 'deg) translateY(-6px)';
        });
      };

      var onLeave = function () {
        if (frame) window.cancelAnimationFrame(frame);
        card.style.transform = '';
      };

      card.addEventListener('mousemove', onMove);
      card.addEventListener('mouseleave', onLeave);
    });
  }

  /* ---------- Home loader (once per session) ---------- */
  function initLoader() {
    var loader = document.getElementById('hy-loader');
    if (!loader) return;

    var seen = false;
    try {
      seen = sessionStorage.getItem('hy-loader-seen') === '1';
    } catch (e) {
      /* storage unavailable — just show once */
    }

    if (seen || reducedMotion) {
      loader.remove();
      return;
    }

    try {
      sessionStorage.setItem('hy-loader-seen', '1');
    } catch (e) {
      /* ignore */
    }

    var dismiss = function () {
      loader.classList.add('done');
      setTimeout(function () {
        loader.remove();
      }, 1000);
    };
    setTimeout(dismiss, 1000);
  }

  /* ---------- Variant switch — premium loading veil ----------
     Variant changes on PDP go through Shopify's Section Rendering API
     (variant-picker.js → fetch → morph/media-gallery replace). That
     fetch can feel like a hang with no feedback. We listen for the
     theme's `shopify:product:select` event to know when a switch starts,
     show a branded veil over the gallery + mute the picker, then hide
     it when `media-gallery` is replaced or the promise settles.
     Works for both success and abort/error — veil always clears. */
  function initVariantSwitchLoader() {
    var main = document.querySelector("main[data-template='product']");
    if (!main) return;
    var mediaWrap = main.querySelector('.product-information__media');
    if (!mediaWrap) return;

    var loader = document.createElement('div');
    loader.className = 'hy-variant-loader';
    loader.setAttribute('aria-hidden', 'true');
    loader.innerHTML =
      '<span class="hy-variant-loader__spinner" aria-hidden="true"></span>' +
      '<span class="hy-variant-loader__label">Updating finish</span>' +
      '<span class="hy-variant-loader__bar" aria-hidden="true"></span>';

    // overlay must sit on the media wrapper (position: relative)
    var cs = getComputedStyle(mediaWrap);
    if (cs.position === 'static') mediaWrap.style.position = 'relative';
    mediaWrap.appendChild(loader);

    var active = false;
    var clearTimer = null;

    var show = function () {
      if (active) return;
      active = true;
      main.classList.add('hy-variant-switching');
      mediaWrap.classList.add('hy-variant-switching');
      if (clearTimer) {
        clearTimeout(clearTimer);
        clearTimer = null;
      }
    };

    var hide = function () {
      if (!active) return;
      active = false;
      main.classList.remove('hy-variant-switching');
      // gallery replace can morph the node; keep both selectors in sync
      var currentWrap = main.querySelector('.product-information__media');
      if (currentWrap) currentWrap.classList.remove('hy-variant-switching');
      if (clearTimer) clearTimeout(clearTimer);
    };

    var scheduleHide = function (delay) {
      if (clearTimer) clearTimeout(clearTimer);
      clearTimer = setTimeout(hide, typeof delay === 'number' ? delay : 220);
    };

    // variant-picker dispatches on the shopify-section or dialog
    var section = main.closest('.shopify-section') || main;
    var wire = section || document;

    wire.addEventListener('shopify:product:select', function (e) {
      if (!(e.target instanceof Element) || e.target.closest('product-card')) return;
      show();
      var p = e.detail && e.detail.promise ? e.detail.promise : e.promise;
      if (p && typeof p.then === 'function') {
        p.then(function () {
          scheduleHide(260);
        }).catch(function () {
          scheduleHide(180);
        });
      } else {
        // Fallback: observe gallery swap or time out
        var obs = new MutationObserver(function () {
          if (main.querySelector('media-gallery')) {
            obs.disconnect();
            scheduleHide(200);
          }
        });
        obs.observe(main, { childList: true, subtree: true });
        setTimeout(function () {
          obs.disconnect();
          hide();
        }, 1400);
      }
    });

    // Safety net: if gallery is replaced without the above (theme-editor,
    // quick morph fallback), hide shortly after.
    var galleryObserver = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].addedNodes && muts[i].addedNodes.length) {
          for (var j = 0; j < muts[i].addedNodes.length; j++) {
            var n = muts[i].addedNodes[j];
            if (n.nodeType === 1 && (n.matches && n.matches('media-gallery'))) {
              scheduleHide(180);
            }
          }
        }
      }
    });
    galleryObserver.observe(mediaWrap, { childList: true, subtree: true });
  }

  /* ---------- Video play ---------- */
  function initVideo() {
    document.querySelectorAll('[data-hy-video]').forEach(function (frame) {
      var btn = frame.querySelector('.hy-play-btn');
      var video = frame.querySelector('video');
      var poster = frame.querySelector('.hy-video__poster');
      if (!btn || !video) return;

      btn.addEventListener('click', function () {
        video.style.display = 'block';
        if (poster) poster.style.display = 'none';
        btn.style.display = 'none';
        video.play().catch(function () {
          video.style.display = 'none';
          if (poster) poster.style.display = 'flex';
          btn.style.display = 'flex';
        });
      });
    });
  }

  function init() {
    initReveals();
    initCounters();
    initTilt();
    initLoader();
    initVariantSwitchLoader();
    initVideo();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-run reveals for content injected by the Shopify theme editor */
  document.addEventListener('shopify:section:load', function () {
    initReveals();
    initCounters();
    initTilt();
    initVariantSwitchLoader();
    initVideo();
  });
})();
